// src/pages/manutencoes/ReciboAvulsoPage.tsx
import React, { useState } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import { FaPrint, FaEraser } from 'react-icons/fa';

// ---------- valor por extenso ----------
function numToWords(n: number): string {
  if (n === 0) return '';
  const uni = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove',
    'dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const dez = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const cen = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos',
    'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
  if (n === 100) return 'cem';
  if (n < 20) return uni[n];
  if (n < 100) { const u = n % 10; return u ? dez[Math.floor(n / 10)] + ' e ' + uni[u] : dez[Math.floor(n / 10)]; }
  if (n < 1000) { const r = n % 100; return r ? cen[Math.floor(n / 100)] + ' e ' + numToWords(r) : cen[Math.floor(n / 100)]; }
  if (n < 1_000_000) {
    const m = Math.floor(n / 1000); const r = n % 1000;
    const milStr = m === 1 ? 'mil' : numToWords(m) + ' mil';
    return r ? milStr + (r < 100 ? ' e ' : ' ') + numToWords(r) : milStr;
  }
  return n.toString();
}

function valorPorExtenso(valor: number): string {
  const inteiro = Math.floor(valor);
  const centavos = Math.round((valor - inteiro) * 100);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const parts: string[] = [];
  if (inteiro > 0) parts.push(cap(numToWords(inteiro)) + (inteiro === 1 ? ' Real' : ' Reais'));
  if (centavos > 0) parts.push(cap(numToWords(centavos)) + (centavos === 1 ? ' Centavo' : ' Centavos'));
  return parts.length ? parts.join(' e ') : 'Zero Reais';
}

function fmtBRL(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------- impressão ----------
const cssRecibo = `
  body { font-family: Arial, sans-serif; margin: 10px; font-size: 13px; }
  .recibo { border: 2px solid #000; padding: 24px 28px; margin-bottom: 8px; }
  .header { text-align: center; margin-bottom: 18px; }
  .header h2 { margin: 0; font-size: 17px; }
  .header p { margin: 2px 0; font-size: 13px; }
  .titulo { text-align: center; font-weight: bold; font-size: 15px; margin-bottom: 20px; letter-spacing: 1px; }
  .corpo { line-height: 1.9; font-size: 13px; margin-bottom: 40px; }
  .rodape { text-align: right; font-size: 13px; margin-top: 20px; }
  .assinatura { display: flex; justify-content: space-between; margin-top: 50px; font-size: 13px; }
  .linha-ass { text-align: center; }
  .separador { border: none; border-top: 2px dashed #666; margin: 24px 0; text-align: center; }
  .separador::after { content: '✂'; font-size: 14px; color: #666; }
  @media print { body { margin: 5px; } @page { margin: 0.8cm; } }
`;

function gerarBlocoAvulso(cliente: string, referente: string, valor: number, data: string, destino: string) {
  const dataFmt = data ? new Date(data + 'T00:00:00').toLocaleDateString('pt-BR') : '___/___/______';
  const valorFmt = `R$ ${fmtBRL(valor)}`;
  const extenso = valorPorExtenso(valor);
  return `
    <div class="recibo">
      <div class="header">
        <h2>Sociedade Amigos de Coqueiros</h2>
        <p>Associação Beneficente Assistencial</p>
        <p>CNPJ 89.786.511/0001-94</p>
        <p>Avenida Presidente Vargas, SN, Centro — Coqueiros do Sul</p>
      </div>
      <div class="titulo">*** RECIBO *** <span style="font-size:11px;font-weight:normal;border:1px solid #000;padding:1px 8px;margin-left:16px;">${destino}</span></div>
      <div class="corpo">
        Recebemos de <strong>${cliente || '___________________________'}</strong>,
        a importância de <strong>${valorFmt} (${extenso})</strong>
        referente a ${referente || '___________________________'}.
      </div>
      <div class="assinatura">
        <div class="linha-ass">
          ____________________________<br>
          <small>Responsável</small>
        </div>
        <div class="rodape">Coqueiros do Sul, ${dataFmt}.</div>
      </div>
    </div>
  `;
}

function printReciboAvulso(cliente: string, referente: string, valor: number, data: string) {
  const html = `<!DOCTYPE html><html>
    <head><meta charset="UTF-8"><title>Recibo Avulso</title>
    <style>${cssRecibo}</style></head>
    <body>
      ${gerarBlocoAvulso(cliente, referente, valor, data, 'Via do Cliente')}
      <div class="separador"></div>
      ${gerarBlocoAvulso(cliente, referente, valor, data, 'Via da Empresa')}
      <script>window.print();</script>
    </body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
}

// ---------- componente ----------
const hoje = new Date().toISOString().split('T')[0];

const ReciboAvulsoPage: React.FC = () => {
  const [cliente, setCliente] = useState('');
  const [referente, setReferente] = useState('');
  const [data, setData] = useState(hoje);
  const [valor, setValor] = useState('');

  const limpar = () => { setCliente(''); setReferente(''); setData(hoje); setValor(''); };

  const valorNum = parseFloat(valor.replace(',', '.')) || 0;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Recibo Avulso</h1>
      </div>

      <Card className="shadow-sm" style={{ maxWidth: 600 }}>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Cliente *</Form.Label>
            <Form.Control
              value={cliente}
              onChange={e => setCliente(e.target.value)}
              placeholder="Nome do cliente"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Referente *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={referente}
              onChange={e => setReferente(e.target.value)}
              placeholder="Descrição do pagamento recebido"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data do Pagamento *</Form.Label>
                <Form.Control
                  type="date"
                  value={data}
                  onChange={e => setData(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Valor (R$) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={valor}
                  onChange={e => setValor(e.target.value)}
                  placeholder="0,00"
                />
              </Form.Group>
            </Col>
          </Row>

          {valorNum > 0 && (
            <p className="text-muted small mb-3">
              Por extenso: <em>{valorPorExtenso(valorNum)}</em>
            </p>
          )}

          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" onClick={limpar}>
              {FaEraser({ className: 'me-2' })} Limpar
            </Button>
            <Button
              variant="primary"
              onClick={() => printReciboAvulso(cliente, referente, valorNum, data)}
              disabled={!cliente || !referente || valorNum <= 0}
            >
              {FaPrint({ className: 'me-2' })} Imprimir Recibo
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default ReciboAvulsoPage;
